import { Checkbox, Drawer, FormControlLabel, Grid, InputAdornment, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { ValidatorForm } from "react-material-ui-form-validator";
import { IUserFineManage } from "../../_common/interface/userFine/iUserFineManage";
import { PrimaryButton } from "../../_components/button/primary.button";
import { DrawerHeader } from "../../_components/drawer/header.drawer";
import { TextValidatorHcs } from "../../_components/textValidator/textValidatorHcs";
import { toastr } from "../../_components/toastr/toastr";
import { getMoneyFormat } from "../../_helpers/format.helper";
import { handleErrorResponse } from "../../_helpers/handleResponse.helper";
import { userFineService } from "../../_services/userFineService.service";
import { useDrawerStyles } from "../../_styles/drawer.styles";

export const UserFineManageDrawer = (params: { isOpen: boolean, userId: string | null, userFineId: number | null, onSave(): void, onClose(): void }) => {

    const resetData = () => ({
        id: 0,
        aspNetUserId: "",
        userCartId: 0,
        name: "",
        description: "",
        durationBan: 0,
        isBannedIndefinitely: false,
        isChargeable: true,
        isPaid: false,
        price: 0.00,
        isActive: true
    });

    const [data, setData] = useState<IUserFineManage>(resetData);

    useEffect(() => {
        if (params.userFineId != null)
            fetchData();
        else
            setData(resetData);
    }, [params.userFineId]);

    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    function fetchData() {
        if (!params.userFineId) return;

        setIsLoading(true)
        return userFineService
            .get(params.userFineId)
            .then(
                (json) => {
                    setData(json);
                }, handleErrorResponse
            )
            .finally(() => {
                setIsLoading(false);
            });
    }

    function submit(e: any) {
        e.preventDefault();
        setIsSaving(true);

        var _data;

        if (data.id == 0) {
            _data = { ...data, aspNetUserId: params.userId };
        }
        else {
            _data = { ...data, aspNetUserId: data.aspNetUserId };
        };

        return userFineService
            .submit(_data)
            .then(
                () => {
                    toastr.success("Saved successfully");
                    params.onSave();
                    setData(resetData);
                },
                (e) => toastr.error(e)
            )
            .finally(() => setIsSaving(false));
    }

    function disable() {
        setIsLoading(true);
        return userFineService
            .disable(params.userFineId)
            .then(
                (d) => {
                    toastr.success("Disabled successfully");
                    fetchData();
                    params.onSave();
                },
                (e) => {
                    toastr.error(e);
                }
            )
            .finally(() => setIsLoading(false));
    }

    function handleChange(event: any) {
        setData((__data) => { return { ...__data, [event.target.name]: event.target.value } });
    };

    function handleCheckboxChange(event: any) {
        setData((__data) => { return { ...__data, [event.target.name]: event.target.checked } });
    };


    return (
        <Drawer
            anchor={"right"}
            variant="persistent"
            open={params.isOpen}
            ModalProps={{
                keepMounted: true
            }}
        >

            <div
                tabIndex={0}
                role="button">

                <div>

                    <DrawerHeader name={(!!params.userFineId) ? "Update Fine" : "Create Fine"} onClose={params.onClose} />

                    <ValidatorForm
                        onSubmit={submit}
                        noValidate>

                        <Grid container spacing={1} sx={useDrawerStyles.container}>

                            <Grid item xs={12}>
                                <TextValidatorHcs
                                    key="name"
                                    name="name"
                                    label="Name"
                                    required
                                    fullWidth
                                    validators={['required']}
                                    errorMessages={['required field']}
                                    value={data.name}
                                    onChange={handleChange}
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <TextValidatorHcs
                                    key="description"
                                    name="description"
                                    label="Description"
                                    fullWidth
                                    required
                                    validators={['required']}
                                    errorMessages={['required field']}
                                    value={data.description}
                                    onChange={handleChange}
                                    multiline
                                    rows={4}
                                />
                            </Grid>

                            {data.isChargeable &&
                                <Grid item xs={12}>
                                    {!data.isPaid ?
                                        <TextValidatorHcs
                                            type="number"
                                            key="price"
                                            name="price"
                                            label="Price"
                                            fullWidth
                                            required
                                            validators={['required']}
                                            errorMessages={['required field']}
                                            value={data.price}
                                            onChange={handleChange}
                                            InputProps={{
                                                startAdornment: <InputAdornment position="start">&euro;</InputAdornment>,
                                            }}
                                        />
                                        : <Typography color="primary">{data.price ? getMoneyFormat(data.price) : ""} paid</Typography>}
                                </Grid>
                            }

                            {!data.isBannedIndefinitely &&
                                <Grid item xs={12}>
                                    <TextValidatorHcs
                                        type="number"
                                        key="durationBan"
                                        name="durationBan"
                                        label="Duration Ban"
                                        fullWidth
                                        required={!data.isBannedIndefinitely}
                                        validators={['required']}
                                        errorMessages={['required field']}
                                        value={data.durationBan}
                                        onChange={handleChange}
                                    />
                                </Grid>
                            }

                            {!data.isPaid &&
                                <Grid item xs={12}>
                                    <FormControlLabel checked={data.isChargeable} name="isChargeable"
                                        control={<Checkbox onChange={handleCheckboxChange} color={"primary"} />}
                                        label="Is Chargeable"
                                    />
                                </Grid>
                            }

                            <Grid item xs={12}>
                                <FormControlLabel checked={data.isBannedIndefinitely} name="isBannedIndefinitely"
                                    control={<Checkbox onChange={handleCheckboxChange} color={data.isBannedIndefinitely ? "error" : "primary"} />}
                                    label="Is Banned Indefinitely"
                                />
                            </Grid>


                        </Grid>

                        <Grid container spacing={1} sx={useDrawerStyles.container}>

                            <Grid item xs={(data.id == 0 ? 12 : data?.isActive === false ? 12 : 6)}>
                                <PrimaryButton
                                    isSaving={isSaving}
                                    isLoading={isLoading}
                                    type="submit"
                                    variant="save">{"Save"}
                                </PrimaryButton>
                            </Grid>

                            {(data.id > 0) &&
                                <Grid item xs={6}>
                                    <PrimaryButton
                                        type="button"
                                        variant="danger"
                                        disabled={isLoading}
                                        onClick={disable}
                                        icon="remove_circle"
                                    >
                                        Remove
                                    </PrimaryButton>
                                </Grid>
                            }

                        </Grid>

                    </ValidatorForm>
                </div>
            </div>
        </Drawer>
    );
};